import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import dayjs from 'dayjs';
import React, { KeyboardEventHandler, PropsWithChildren, useContext, useRef } from 'react';
import { fullMonths } from '../../../../helpers/localization/localizationUtils';
import Box from '../../../elements/Box';
import Button from '../../../elements/Button';
import Icon from '../../../elements/Icon';
import { CommonCalendarState } from '../types';
import { subMonthsNumber, isDateOutOfView } from './helper';
import { CalendarDispatchAction, useCalendarActions } from './hooks';

export type CalendarHeaderProps = {
  showDecreaseYearButton: boolean;
  showDecreaseMonthButton: boolean;
  showIncreaseYearButton: boolean;
  showIncreaseMonthButton: boolean;
  enabledDecreaseYearButton: boolean;
  enabledIncreaseYearButton: boolean;
  iconNextProps: any;
  iconPrevProps: any;
  iconPrevYearProps: any;
  iconNextYearProps: any;
  containerSettings: any;
  style: any;
  dispatch: CalendarDispatchAction;
  state: CommonCalendarState;
  numberOfMonths: number;
  format?: string;
  currentMonth?: number;
  accessibilityState?: boolean;
  focusOnlyUserSelected?: boolean;
  isPrimaryFilter?: boolean;
  currentMonthDateA11y?: Date;
  setPreselectedDate?: (e: any) => void;
  maxTripDuration?: number;
  minDate?: Date;
  maxDate?: Date;
};

const TAB_KEY = 9;
const CalendarHeader = (props: PropsWithChildren<CalendarHeaderProps>) => {
  const {
    showDecreaseYearButton = false,
    showDecreaseMonthButton = true,
    showIncreaseYearButton = false,
    showIncreaseMonthButton = true,
    enabledDecreaseYearButton = false,
    enabledIncreaseYearButton = false,
    dispatch,
    state,
    numberOfMonths,
    iconNextProps = {},
    iconPrevProps = {},
    iconNextYearProps = {},
    iconPrevYearProps = {},
    containerSettings = {},
    children,
    currentMonth,
    accessibilityState,
    focusOnlyUserSelected = true,
    currentMonthDateA11y,
    isPrimaryFilter = true,
    maxTripDuration,
    minDate,
    maxDate
  } = props;

  const context = useContext(Context);
  const { direction, theme, translate, localizationData } = context;
  const [className] = useStyles(props, { direction, theme });
  const [containerClassName, containerStyle] = useStyles(containerSettings, { direction, theme });
  const previousMonthRef = useRef<HTMLButtonElement>(null);
  const nextMonthRef = useRef<HTMLButtonElement>(null);
  const renderMessagesIsDisablesNextButton = useRef(false);
  const renderMessagesIsDisablesPreviousButton = useRef(false);
  const [firstLoad, setFirstLoad] = React.useState(true);
  const {
    setNextMonth,
    setPreviousMonth,
    setNextYear,
    setPreviousYear,
    isEnabledNextMonth,
    isEnabledPreviousMonth
  } = useCalendarActions(focusOnlyUserSelected, maxTripDuration, minDate, maxDate);

  const date = state.months[0].firstAvailableDay();
  const monthsLabels = fullMonths(translate, localizationData);

  const previousMonth = subMonthsNumber(
    state.displayDate ? state.displayDate : state.today,
    numberOfMonths === 1 ? numberOfMonths : numberOfMonths - 1
  );

  const handleKeyDown: KeyboardEventHandler = (event) => {
    if (event.key === 'ArrowLeft' && isEnabledPreviousMonth) {
      setPreviousMonth();
      dispatch({ type: 'SET_PRESELECTED_DATE', payload: date });
      previousMonthRef.current?.focus();
    } else if (event.key === 'ArrowRight' && isEnabledNextMonth) {
      setNextMonth();
      dispatch({ type: 'SET_PRESELECTED_DATE', payload: date });
      nextMonthRef.current?.focus();
    }
  };
  const [renderA11yMessage, setRenderA11yMessage] = React.useState<false | true | undefined>(
    undefined
  );

  React.useEffect(() => {
    setRenderA11yMessage(false);
    setTimeout(() => {
      setRenderA11yMessage(true);
    }, 300);
  }, [props.currentMonth]);

  React.useEffect(() => {
    if (!firstLoad) {
      if (!isEnabledNextMonth) {
        previousMonthRef.current?.focus();
      }
      if (!isEnabledPreviousMonth) {
        nextMonthRef.current?.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabledNextMonth, isEnabledPreviousMonth]);

  React.useEffect(() => {
    setFirstLoad(false);
  }, []);

  const renderAccessibilityMessages = (
    <div className="fr-sr-only" aria-live="polite">
      {accessibilityState && renderA11yMessage ? (
        <>
          {`
  ${translate('cmp-datepicker-show-month', 'Current month')} 
  ${
    monthsLabels[
      new Date(
        currentMonthDateA11y ? currentMonthDateA11y.getFullYear() : previousMonth.getFullYear(),
        numberOfMonths === 1
          ? currentMonthDateA11y
            ? currentMonthDateA11y.getMonth()
            : previousMonth.getMonth() + numberOfMonths
          : currentMonthDateA11y
          ? currentMonthDateA11y.getMonth()
          : previousMonth.getMonth() + numberOfMonths - 1,
        1
      ).getMonth()
    ]
  }
  ${new Date(
    currentMonthDateA11y ? currentMonthDateA11y.getFullYear() : previousMonth.getFullYear(),
    numberOfMonths === 1
      ? currentMonthDateA11y
        ? currentMonthDateA11y.getMonth()
        : previousMonth.getMonth() + numberOfMonths
      : currentMonthDateA11y
      ? currentMonthDateA11y.getMonth()
      : previousMonth.getMonth() + numberOfMonths - 1,
    1
  ).getFullYear()}
`}
          {currentMonth && currentMonth >= 10 && isEnabledPreviousMonth
            ? `
    ${translate('cmp-datepicker-prev-month', 'Previous month')},
    ${
      monthsLabels[
        new Date(
          currentMonthDateA11y ? currentMonthDateA11y.getFullYear() : previousMonth.getFullYear(),
          numberOfMonths === 1
            ? currentMonthDateA11y
              ? dayjs(currentMonthDateA11y).subtract(1, 'month').toDate().getMonth()
              : previousMonth.getMonth()
            : currentMonthDateA11y
            ? dayjs(currentMonthDateA11y).subtract(1, 'month').toDate().getMonth()
            : previousMonth.getMonth() + numberOfMonths - 2,
          1
        ).getMonth()
      ]
    }
    `
            : !isEnabledPreviousMonth &&
              dayjs(state.displayDate).get('month') > dayjs(state.today).get('month') &&
              currentMonth &&
              currentMonth >= 10
            ? `
          ${translate('cmp-datepicker-prev-month', 'Previous month')},
          ${
            monthsLabels[
              new Date(
                currentMonthDateA11y
                  ? currentMonthDateA11y.getFullYear()
                  : previousMonth.getFullYear(),
                numberOfMonths === 1
                  ? currentMonthDateA11y
                    ? dayjs(currentMonthDateA11y).subtract(1, 'month').toDate().getMonth()
                    : previousMonth.getMonth()
                  : currentMonthDateA11y
                  ? dayjs(currentMonthDateA11y).subtract(1, 'month').toDate().getMonth()
                  : previousMonth.getMonth() + numberOfMonths - 2,
                1
              ).getMonth()
            ]
          }
          `
            : null}

          {currentMonth && currentMonth >= 5 && currentMonth < 10 && isEnabledNextMonth
            ? `
    ${translate('cmp-datepicker-next-month', 'Next month')},
    ${
      monthsLabels[
        new Date(
          currentMonthDateA11y ? currentMonthDateA11y.getFullYear() : previousMonth.getFullYear(),
          numberOfMonths === 1
            ? currentMonthDateA11y
              ? dayjs(currentMonthDateA11y).add(1, 'month').toDate().getMonth()
              : previousMonth.getMonth() + numberOfMonths + 1
            : currentMonthDateA11y
            ? dayjs(currentMonthDateA11y).add(1, 'month').toDate().getMonth()
            : previousMonth.getMonth() + numberOfMonths,
          1
        ).getMonth()
      ]
    }
    `
            : !isEnabledNextMonth &&
              dayjs(state.displayDate).get('month') <
                dayjs(state.today)
                  .add(state.availableDays ?? 0, 'day')
                  .get('month') &&
              currentMonth &&
              currentMonth >= 5 &&
              currentMonth < 10
            ? `
                       ${translate('cmp-datepicker-next-month', 'Next month')},
                       ${
                         monthsLabels[
                           new Date(
                             currentMonthDateA11y
                               ? currentMonthDateA11y.getFullYear()
                               : previousMonth.getFullYear(),
                             numberOfMonths === 1
                               ? currentMonthDateA11y
                                 ? dayjs(currentMonthDateA11y).add(1, 'month').toDate().getMonth()
                                 : previousMonth.getMonth() + numberOfMonths + 1
                               : currentMonthDateA11y
                               ? dayjs(currentMonthDateA11y).add(1, 'month').toDate().getMonth()
                               : previousMonth.getMonth() + numberOfMonths,
                             1
                           ).getMonth()
                         ]
                       }
                       `
            : null}
          {renderMessagesIsDisablesNextButton.current
            ? translate('cmp-datepicker-next-month-button-disabled', 'Next month button disabled')
            : ''}
          {renderMessagesIsDisablesPreviousButton.current
            ? translate(
                'cmp-datepicker-previous-month-button-disabled',
                'Previous month button disabled'
              )
            : ''}
        </>
      ) : null}
    </div>
  );

  return (
    <Box
      className={`calendar-header ${containerClassName}`}
      style={containerStyle}
      data-testid="calendar-header"
      onKeyDown={handleKeyDown}
    >
      {renderAccessibilityMessages}
      <div className="calendar-header-button-container">
        <div className="btn-wrapper-left fr-d-flex fr-align-items-center">
          {showDecreaseYearButton && (
            <button
              type="button"
              name={'Previous year'}
              data-testid="previousYearBtn"
              disabled={!enabledDecreaseYearButton}
              aria-disabled={!enabledDecreaseYearButton}
              onClick={setPreviousYear}
              className={`calendar-button fr-mr-1 ${className}`}
              // tabIndex={tabIndex}
            >
              <Icon
                icon={direction === 'rtl' ? 'right-chevron' : 'left-chevron'}
                {...iconPrevProps}
                {...iconPrevYearProps}
              />
            </button>
          )}
          {showDecreaseMonthButton && (
            <Button
              focusReferences={previousMonthRef}
              type="button"
              cursor={!isEnabledPreviousMonth ? 'default' : 'pointer'}
              name={translate('cmp-datepicker-prev-month', 'Previous month')}
              dataTestId="previousMonthBtn"
              disabled={!isEnabledPreviousMonth}
              ariaDisabled={!isEnabledPreviousMonth}
              onClick={() => {
                if (isEnabledPreviousMonth) {
                  const newCurrentMonth = Math.random() + 10;
                  dispatch({
                    type: 'CHANGE_CURRENT_MONTH',
                    payload: newCurrentMonth
                  });
                  dispatch({
                    type: 'SET_CURRENT_MONTH_DATE_A11Y',
                    payload: dayjs(currentMonthDateA11y).subtract(1, 'month').toDate()
                  });
                  setPreviousMonth();
                  dispatch({ type: 'SET_FOCUS_SELECTED_DAY', payload: false });
                } else {
                  renderMessagesIsDisablesPreviousButton.current = true;
                  const newCurrentMonth = Math.random() + 10;
                  dispatch({
                    type: 'CHANGE_CURRENT_MONTH',
                    payload: newCurrentMonth
                  });
                  setTimeout(() => {
                    renderMessagesIsDisablesPreviousButton.current = false;
                  }, 500);
                }
              }}
              onKeyDown={(e) => {
                if (!isEnabledNextMonth) {
                  if (isPrimaryFilter) {
                    if (
                      !state.preSelected &&
                      props.setPreselectedDate &&
                      e.keyCode === TAB_KEY &&
                      !e.shiftKey
                    ) {
                      e.preventDefault();
                      props.setPreselectedDate(e);
                    }
                  }
                  if (
                    e.keyCode === TAB_KEY &&
                    !e.shiftKey &&
                    state.months.length > 0 &&
                    isDateOutOfView(
                      state.preSelected ?? state.today,
                      numberOfMonths,
                      state.displayDate
                    )
                  ) {
                    dispatch({
                      type: 'SET_PRESELECTED_DATE',
                      payload: state.months[0].firstAvailableDay()
                    });
                  }
                }
              }}
              className={`calendar-button ${className} ${
                !isEnabledPreviousMonth ? 'fr-btn-disabled' : ''
              }`}
              ariaLabel={translate(
                'cmp-datepicker-previous-month-accessibility-aria-label',
                'Previous month'
              )}
            >
              <Icon
                icon={direction === 'rtl' ? 'right-chevron' : 'left-chevron'}
                {...iconPrevProps}
              />
            </Button>
          )}
        </div>
        <div className="btn-wrapper-right fr-d-flex fr-align-items-center">
          {showIncreaseMonthButton && (
            <Button
              focusReferences={nextMonthRef}
              type="button"
              cursor={!isEnabledNextMonth ? 'default' : 'pointer'}
              name={translate('cmp-datepicker-next-month', 'Next month')}
              dataTestId="nextMonthBtn"
              disabled={!isEnabledNextMonth}
              ariaDisabled={!isEnabledNextMonth}
              ariaLabel={translate(
                'cmp-datepicker-next-month-accessibility-aria-label',
                'Next month'
              )}
              onClick={() => {
                if (isEnabledNextMonth) {
                  const newCurrentMonth = Math.random() + 5;
                  dispatch({
                    type: 'CHANGE_CURRENT_MONTH',
                    payload: newCurrentMonth
                  });
                  setNextMonth();
                  dispatch({ type: 'SET_FOCUS_SELECTED_DAY', payload: false });
                  dispatch({
                    type: 'SET_CURRENT_MONTH_DATE_A11Y',
                    payload: dayjs(currentMonthDateA11y).add(1, 'month').toDate()
                  });
                } else {
                  renderMessagesIsDisablesNextButton.current = true;
                  const newCurrentMonth = Math.random() + 5;
                  dispatch({
                    type: 'CHANGE_CURRENT_MONTH',
                    payload: newCurrentMonth
                  });
                  setTimeout(() => {
                    renderMessagesIsDisablesNextButton.current = false;
                  }, 500);
                }
              }}
              onKeyDown={(e) => {
                if (
                  !state.preSelected &&
                  props.setPreselectedDate &&
                  e.keyCode === TAB_KEY &&
                  !e.shiftKey
                ) {
                  e.preventDefault();
                  props.setPreselectedDate(e);
                }
                if (
                  e.keyCode === TAB_KEY &&
                  !e.shiftKey &&
                  state.months.length > 0 &&
                  isDateOutOfView(
                    state.preSelected ?? state.today,
                    numberOfMonths,
                    state.displayDate
                  )
                ) {
                  dispatch({
                    type: 'SET_PRESELECTED_DATE',
                    payload: state.months[0].firstAvailableDay()
                  });
                }
                if (e.keyCode === TAB_KEY && !e.shiftKey && state.months.length > 0) {
                  if (state.selectedComponent === 'end') {
                    dispatch({
                      type: 'SET_VALID_END',
                      payload: true
                    });
                  }
                }
              }}
              className={`calendar-button ${className} ${
                !isEnabledNextMonth ? 'fr-btn-disabled' : ''
              }`}
            >
              <Icon
                icon={direction === 'rtl' ? 'left-chevron' : 'right-chevron'}
                {...iconNextProps}
              />
            </Button>
          )}
          {showIncreaseYearButton && (
            <button
              type="button"
              name={'Next year'}
              data-testid="nextYearBtn"
              disabled={!enabledIncreaseYearButton}
              aria-disabled={!enabledIncreaseYearButton}
              onClick={setNextYear}
              className={`calendar-button fr-ml-1 ${className}`}
            >
              <Icon
                icon={direction === 'rtl' ? 'left-chevron' : 'right-chevron'}
                {...iconNextProps}
                {...iconNextYearProps}
              />
            </button>
          )}
        </div>
      </div>
      {children}
    </Box>
  );
};

export default CalendarHeader;
